import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { getTurnstileSiteKey } from '../services/authApi';

// Carga el script de Cloudflare Turnstile una sola vez para toda la app.
// Cachea la promise para que múltiples widgets no descarguen el script varias veces.
let scriptPromise = null;
function loadTurnstileScript() {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (window.turnstile) return resolve();
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true;
    s.defer = true;
    s.onload = () => {
      // Esperar hasta que window.turnstile esté disponible
      const waitFor = (retries = 40) => {
        if (window.turnstile) return resolve();
        if (retries <= 0) return reject(new Error('Turnstile no cargó a tiempo'));
        setTimeout(() => waitFor(retries - 1), 100);
      };
      waitFor();
    };
    s.onerror = () => reject(new Error('No se pudo cargar Turnstile'));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

// Cachea la sitekey para evitar peticiones repetidas
let siteKeyCached = null;
let siteKeyPromise = null;
function fetchSiteKey() {
  if (siteKeyCached) return Promise.resolve(siteKeyCached);
  if (siteKeyPromise) return siteKeyPromise;
  siteKeyPromise = getTurnstileSiteKey()
    .then(resp => { siteKeyCached = resp?.siteKey || ''; return siteKeyCached; })
    .catch(() => '');
  return siteKeyPromise;
}

/**
 * Widget Turnstile invisible.
 *
 * Props:
 * - onToken(token): callback llamado cuando el widget verifica exitosamente
 *   y emite un token. Este token debe enviarse al backend en el request.
 * - onError(err): opcional, callback cuando algo falla.
 * - onExpired(): opcional, callback cuando el token expira antes de usarse.
 * - action: opcional, string tipo "login" / "register" / "forgot" para logs.
 *
 * El widget se renderiza como invisible (0px) pero está presente en el DOM.
 * Al montar, ejecuta la verificación automáticamente y emite el token.
 *
 * Uso típico:
 *   const [tsToken, setTsToken] = useState('');
 *   <TurnstileWidget onToken={setTsToken} action="login" />
 *   // Al hacer submit, incluir tsToken en el request
 */
export default function TurnstileWidget({ onToken, onError, onExpired, action = 'default' }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    let cancelado = false;

    (async () => {
      try {
        const [_, siteKey] = await Promise.all([loadTurnstileScript(), fetchSiteKey()]);
        if (cancelado) return;

        if (!siteKey) {
          onError?.(new Error('Site key de Turnstile no configurada en el backend'));
          return;
        }

        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action,
          callback: (token) => {
            onToken?.(token);
          },
          'error-callback': (code) => {
            onError?.(new Error(`Turnstile error: ${code}`));
          },
          'expired-callback': () => {
            onExpired?.();
            // Auto-refrescar el token
            if (widgetIdRef.current != null && window.turnstile) {
              try { window.turnstile.reset(widgetIdRef.current); } catch (_) {}
            }
          },
        });


      } catch (err) {
        if (!cancelado) {
          onError?.(err);
        }
      }
    })();

    return () => {
      cancelado = true;
      if (widgetIdRef.current != null && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (_) {}
        widgetIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, mb: 1 }}>
      {/* Contenedor del widget Turnstile (modo Managed - visible) */}
      <div ref={containerRef}/>
    </Box>
  );
}
